import React, { useEffect, useState } from "react";
import apiClient from "../../../utils/apiClient";
import SignalUpdateTable from "./SignalUpdateTable"
import server from "../../../utils/serverConfig";

export default function SignalUpdateData() {
  const [signalData, setSignalData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Date states for filtering (YYYY-MM-DD)
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  // Tab state
  const [selectedVersion, setSelectedVersion] = useState("V5");

  const [symbolSearch, setSymbolSearch] = useState("");
  const [pdiPdiMaCross, setPdiPdiMaCross] = useState("ALL");
  const [pdiMdiCross, setPdiMdiCross] = useState("ALL");
  const [hhpcCross, setHhpcCross] = useState("ALL");

  const versionToTable = {
    "V5": "options_v5_fyers_60min",
    "V5_2": "options_v5_2_fyers_60min",
    "V6": "options_v6_fyers_60min"
  };

  const fetchSignalData = () => {
    setLoading(true);
    let config = {
      method: "get",
      url: `${server.godseye.baseUrl}signal_update`,
      params: {
          start_date: startDate,
          end_date: endDate,
          table_name: versionToTable[selectedVersion]
      }
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Signal update data", response.data);
        if (response.data && response.data.data) {
             setSignalData(response.data.data);
        } else {
             setSignalData([]);
        }
      })
      .catch((error) => {
        console.log(error);
        setSignalData([]);
      })
      .finally(() => {
          setLoading(false);
      });
  };

  useEffect(() => { 
    fetchSignalData();
  }, [startDate, endDate, selectedVersion]); // Refetch when dates or version change

  const handleDateFilter = (e) => {
      e.preventDefault();
      fetchSignalData();
  };

  // Client-side filtering
  const filteredData = signalData.filter(item => {
      // Symbol Match (checks scrip_code)
      const symbolMatch = symbolSearch === "" || (item.scrip_code && String(item.scrip_code).toLowerCase().includes(symbolSearch.toLowerCase()));
      
      // Column matches
      const matchCross = (val, filterVal) => {
          if (filterVal === "ALL") return true;
          // Exact match
          return String(val) === String(filterVal);
      };

      const pdi1Match = matchCross(item.pdi_pdi_ma_cross, pdiPdiMaCross);
      const pdi2Match = matchCross(item.pdi_mdi_cross, pdiMdiCross);
      const hhpcMatch = matchCross(item.hhpc_cross, hhpcCross);

      return symbolMatch && pdi1Match && pdi2Match && hhpcMatch;
  });

  return (
    <div className="flex flex-col relative w-full h-[calc(100vh-110px)] overflow-hidden">
      <div className="flex flex-col space-y-2 px-6 py-2 w-full border-b border-gray-800">
        <div className="flex justify-between items-center h-[50px] w-full">
          <div className="flex items-center space-x-6">
            <p className="text-lg font-semibold text-white">
              Signal Update Table
            </p>
            
            {/* Version Tabs */}
            <div className="flex bg-[#1A1A1A] p-0.5 rounded-lg border border-gray-800">
              {Object.keys(versionToTable).map((v) => (
                <button
                  key={v}
                  onClick={() => setSelectedVersion(v)}
                  className={`px-4 py-1 rounded-md text-sm font-medium transition-all ${
                    selectedVersion === v
                      ? "bg-blue-600 text-white shadow-lg"
                      : "text-gray-400 hover:text-white hover:bg-gray-800"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleDateFilter} className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                  <label className="text-gray-300 text-sm">Start:</label>
                  <input 
                      type="date" 
                      value={startDate} 
                      onChange={(e) => setStartDate(e.target.value)}
                      className="bg-[#1A1A1A] text-white border border-gray-700 px-3 py-1 rounded text-sm focus:outline-none focus:border-blue-500"
                  />
              </div>
              <div className="flex items-center space-x-2">
                  <label className="text-gray-300 text-sm">End:</label>
                  <input 
                      type="date" 
                      value={endDate} 
                      onChange={(e) => setEndDate(e.target.value)}
                      className="bg-[#1A1A1A] text-white border border-gray-700 px-3 py-1 rounded text-sm focus:outline-none focus:border-blue-500"
                  />
              </div>
              <button 
                  type="submit" 
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded text-sm transition-colors disabled:opacity-50"
              >
                  {loading ? 'Loading...' : 'Fetch Dates'}
              </button>
          </form>
        </div>

        {/* Client side filters */}
        <div className="flex items-center justify-end space-x-4 w-full text-sm">
            <div className="flex items-center space-x-2">
                <label className="text-gray-400">Symbol:</label>
                <input 
                    type="text" 
                    placeholder="Search Symbol"
                    value={symbolSearch}
                    onChange={(e) => setSymbolSearch(e.target.value)}
                    className="bg-[#1A1A1A] text-white border border-gray-700 px-2 py-1 rounded text-sm focus:outline-none focus:border-blue-500 w-[150px]"
                />
            </div>

            <div className="flex items-center space-x-2">
                <label className="text-gray-400">pdi_pdi_ma_cross:</label>
                <select value={pdiPdiMaCross} onChange={(e) => setPdiPdiMaCross(e.target.value)} className="bg-[#1A1A1A] text-white border border-gray-700 px-2 py-1.5 rounded focus:outline-none focus:border-blue-500 min-w-[60px]">
                    <option value="ALL">ALL</option>
                    <option value="0">0</option>
                    <option value="1">1</option>
                    <option value="N/A">N/A</option>
                </select>
            </div>

            <div className="flex items-center space-x-2">
                <label className="text-gray-400">pdi_mdi_cross:</label>
                <select value={pdiMdiCross} onChange={(e) => setPdiMdiCross(e.target.value)} className="bg-[#1A1A1A] text-white border border-gray-700 px-2 py-1.5 rounded focus:outline-none focus:border-blue-500 min-w-[60px]">
                    <option value="ALL">ALL</option>
                    <option value="0">0</option>
                    <option value="1">1</option>
                    <option value="N/A">N/A</option>
                </select>
            </div>

            <div className="flex items-center space-x-2">
                <label className="text-gray-400">hhpc_cross:</label>
                <select value={hhpcCross} onChange={(e) => setHhpcCross(e.target.value)} className="bg-[#1A1A1A] text-white border border-gray-700 px-2 py-1.5 rounded focus:outline-none focus:border-blue-500 min-w-[60px]">
                    <option value="ALL">ALL</option>
                    <option value="0">0</option>
                    <option value="1">1</option>
                    <option value="N/A">N/A</option>
                </select>
            </div>
        </div>
      </div>

      <div className="w-full flex-1 overflow-hidden mt-4 px-6 pb-4">
        <SignalUpdateTable signalData={filteredData} />
      </div>
    </div>
  );
}
